import { Spinner } from '@/components/glitch-ui/spinner';
import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useState, useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Sparkles, AlertCircle, Search, Zap, Target, Clock, Coins, Ban } from 'lucide-react';
import { DataTable } from '@/components/glitch-ui/data-table';
import { SortableHeader } from '@/components/glitch-ui/data-table/sortable-header';
import { PageErrorState } from '@/components/glitch-ui/empty-state';
import { Card } from '@/components/ui/card';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { Button } from '@/components/ui/button';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import { Input } from '@/components/ui/input';

import { fetchAllActionLogs, confirmOptimizations, addCampaignNegativeKeywords, type ConfirmOptimizationAction, type SearchTermMetrics } from '@/lib/apis';
import { ActionType, UserRole } from '@/lib/types';
import type { ActionLog, MatchedRule, ComponentScore } from '@/lib/types';
import { useUserProfile } from '@/contexts/user.context';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchTermsReportQuery } from '@/lib/query-options';
import { useAtomValue } from 'jotai';
import { organizationDataAtom, campaignsDataAtom, selectedOptimizationsTabAtom, googleAdsAccountAtom } from '@/store';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { usePostHog } from 'posthog-js/react';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useTestMode } from '@/hooks/useTestMode';
import { withOrgReset } from '@/components/glitch-ui/with-org-reset';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { FilterDropdown, type FilterDropdownState } from '@/components/glitch-ui/filter-dropdown';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MATCH_TYPE_COLORS } from '@/lib/match-type';

// Estimated minutes saved per optimization (manual review overhead)
const MINUTES_PER_OPTIMIZATION = 2;

// Summary Metrics Component - calculates real metrics from action logs
function OptimizationSummaryMetrics({
  currencySymbol,
  actionLogs,
}: {
  currencySymbol: string;
  actionLogs: ActionLog[];
}) {
  const metrics = useMemo(() => {
    const optimizationsMade = actionLogs.length;

    // Count unique search terms analysed (keyword entities)
    const searchTerms = new Set<string>();
    actionLogs.forEach((action) => {
      if (action.entity_type === 'keyword' || action.entity_type === 'negative_keyword') {
        const term =
          (action.after_state?.keyword_text as string) ||
          (action.after_state?.keyword as string) ||
          (action.after_state?.negative_keyword as string) ||
          (action.details?.keyword_text as string);
        if (term) searchTerms.add(term.toLowerCase());
      }
    });

    // Accuracy: average confidence level from matched rules
    let totalConfidence = 0;
    let confidenceCount = 0;
    actionLogs.forEach((action) => {
      const confidence = action.after_state?.matched_rules?.[0]?.confidence_level;
      if (confidence != null) {
        totalConfidence += confidence;
        confidenceCount++;
      }
    });
    const accuracy = confidenceCount > 0 ? (totalConfidence / confidenceCount) * 100 : 0;

    // Time saved: estimated minutes per optimization converted to hours
    const timeSaved = (optimizationsMade * MINUTES_PER_OPTIMIZATION) / 60;

    // Budget reallocated: sum of cost from performance metrics
    let budgetReallocated = 0;
    actionLogs.forEach((action) => {
      const cost = action.after_state?.matched_rules?.[0]?.performance_metrics?.cost;
      if (cost != null) budgetReallocated += cost;
    });

    return { searchTermsAnalysed: searchTerms.size, optimizationsMade, accuracy, timeSaved, budgetReallocated };
  }, [actionLogs]);

  return (
    <TooltipProvider delayDuration={200}>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
      <Card className="bg-white p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center gap-2 mb-3 cursor-help">
              <div className="p-1.5 rounded-md bg-blue-50">
                <Search className="h-4 w-4 text-blue-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-700">Search Terms Analysed</h3>
            </div>
          </TooltipTrigger>
          <TooltipContent>Total unique search terms evaluated for optimization</TooltipContent>
        </Tooltip>
        <p className="text-3xl font-bold text-center text-gray-900">
          {metrics.searchTermsAnalysed.toLocaleString()}
        </p>
      </Card>

      <Card className="bg-white p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center gap-2 mb-3 cursor-help">
              <div className="p-1.5 rounded-md bg-green-50">
                <Zap className="h-4 w-4 text-green-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-700">Optimizations Made</h3>
            </div>
          </TooltipTrigger>
          <TooltipContent>Number of optimization actions pending review</TooltipContent>
        </Tooltip>
        <p className="text-3xl font-bold text-center text-gray-900">
          {metrics.optimizationsMade}
        </p>
      </Card>

      <Card className="bg-white p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center gap-2 mb-3 cursor-help">
              <div className="p-1.5 rounded-md bg-purple-50">
                <Target className="h-4 w-4 text-purple-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-700">Accuracy</h3>
            </div>
          </TooltipTrigger>
          <TooltipContent>Average confidence score of the optimization algorithm</TooltipContent>
        </Tooltip>
        <p className="text-3xl font-bold text-center text-gray-900">
          {metrics.accuracy > 0 ? `${metrics.accuracy.toFixed(1)}%` : '-'}
        </p>
      </Card>

      <Card className="bg-white p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center gap-2 mb-3 cursor-help">
              <div className="p-1.5 rounded-md bg-amber-50">
                <Clock className="h-4 w-4 text-amber-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-700">Time Saved</h3>
            </div>
          </TooltipTrigger>
          <TooltipContent>Estimated hours saved vs manual review (~2 min per optimization)</TooltipContent>
        </Tooltip>
        <p className="text-3xl font-bold text-center text-gray-900">
          {metrics.timeSaved > 0 ? `${metrics.timeSaved.toFixed(1)}h` : '-'}
        </p>
      </Card>

      <Card className="bg-white p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center gap-2 mb-3 cursor-help">
              <div className="p-1.5 rounded-md bg-teal-50">
                <Coins className="h-4 w-4 text-teal-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-700">Budget Reallocated</h3>
            </div>
          </TooltipTrigger>
          <TooltipContent>Total ad spend associated with optimized keywords</TooltipContent>
        </Tooltip>
        <p className="text-3xl font-bold text-center text-gray-900">
          {metrics.budgetReallocated > 0 ? `${currencySymbol}${metrics.budgetReallocated.toFixed(2)}` : '-'}
        </p>
      </Card>
    </div>
    </TooltipProvider>
  );
}

// Tab type definitions
const TAB_IDS = ['actions', 'search-terms'] as const;

const tabs = [
  { id: 'actions' as const, label: 'AI Actions' },
  { id: 'search-terms' as const, label: 'Search Terms' },
];

// Parse campaign name to short display format (matching sidebar)
function parseCampaignName(name: string): string {
  const parts = name.split(' | ');
  if (parts.length >= 3 && parts[0] === 'Glitch') {
    if (parts.length === 4) {
      if (parts[1] === 'Geo') return `${parts[3]} | ${parts[2]}`;
      return parts[3];
    }
    return parts[2];
  }
  return name;
}

// Search Terms Tab Component (RMF R.70 compliance)
function SearchTermsTab({ currencySymbol, days, searchFilter }: { currencySymbol: string; days: number; searchFilter: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const organizationData = useAtomValue(organizationDataAtom);
  const isTestMode = useTestMode();
  const campaigns = useAtomValue(campaignsDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const [selectedTerms, setSelectedTerms] = useState<Set<string>>(new Set());

  // Map Google Ads campaign ID → local campaign info for display and API calls
  const campaignNameMap = useMemo(() => {
    const map = new Map<number, string>();
    campaigns.forEach((c) => {
      if (c.googleads_campaign?.googleads_campaign_id) {
        map.set(Number(c.googleads_campaign.googleads_campaign_id), c.name);
      }
    });
    return map;
  }, [campaigns]);

  // Map Google Ads campaign ID → local DB campaign ID for API calls
  const campaignIdMap = useMemo(() => {
    const map = new Map<number, number>();
    campaigns.forEach((c) => {
      if (c.googleads_campaign?.googleads_campaign_id) {
        map.set(Number(c.googleads_campaign.googleads_campaign_id), c.id);
      }
    });
    return map;
  }, [campaigns]);

  const {
    data: reportData,
    isLoading,
    error,
  } = useSearchTermsReportQuery(days, organizationData?.id);

  const searchTerms = reportData?.search_terms || [];

  // Filter search terms (sorting handled by DataTable)
  const filteredTerms = useMemo(() => {
    if (!searchFilter) return searchTerms;
    const lowerFilter = searchFilter.toLowerCase();
    return searchTerms.filter(
      (term) => {
        const localName = campaignNameMap.get(term.campaign_id);
        return (
          term.search_term.toLowerCase().includes(lowerFilter) ||
          (localName?.toLowerCase().includes(lowerFilter)) ||
          term.campaign_name.toLowerCase().includes(lowerFilter) ||
          term.ad_group_name.toLowerCase().includes(lowerFilter)
        );
      }
    );
  }, [searchTerms, searchFilter, campaignNameMap]);

  // Toggle term selection
  const toggleTermSelection = (searchTerm: string) => {
    setSelectedTerms((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(searchTerm)) {
        newSet.delete(searchTerm);
      } else {
        newSet.add(searchTerm);
      }
      return newSet;
    });
  };

  // Select all visible terms
  const selectAllVisible = () => {
    const allVisible = new Set(filteredTerms.map((t) => t.search_term));
    setSelectedTerms(allVisible);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedTerms(new Set());
  };

  // Add selected terms as negative keywords, grouped by campaign
  const [isAddingNegatives, setIsAddingNegatives] = useState(false);
  const handleAddAsNegative = async () => {
    if (selectedTerms.size === 0) return;
    setIsAddingNegatives(true);

    try {
      // Group selected terms by local DB campaign ID (mapped from Google Ads campaign ID)
      const termsByCampaign = new Map<number, string[]>();
      const skippedTerms: string[] = [];
      for (const term of filteredTerms) {
        if (!selectedTerms.has(term.search_term)) continue;
        const localCampaignId = campaignIdMap.get(term.campaign_id);
        if (!localCampaignId) {
          skippedTerms.push(term.search_term);
          continue;
        }
        const existing = termsByCampaign.get(localCampaignId) || [];
        if (!existing.includes(term.search_term)) {
          existing.push(term.search_term);
        }
        termsByCampaign.set(localCampaignId, existing);
      }
      // Add negatives per campaign
      let totalAdded = 0;
      const successfulCampaignIds: number[] = [];
      const errors: string[] = [];
      if (skippedTerms.length > 0) {
        errors.push(`Could not map ${skippedTerms.length} term(s) to local campaigns`);
      }
      for (const [campaignId, keywords] of termsByCampaign) {
        try {
          await addCampaignNegativeKeywords(campaignId, {
            keywords: keywords.map((kw) => ({ keyword: kw, match_type: 'EXACT' as const })),
          });
          totalAdded += keywords.length;
          successfulCampaignIds.push(campaignId);
        } catch (err) {
          errors.push(`Campaign ${campaignId}: ${err instanceof Error ? err.message : 'Failed'}`);
        }
      }

      // Analytics and cache invalidation for successful campaigns
      for (const campaignId of successfulCampaignIds) {
        posthog?.capture('negative_keywords_added', { campaign_id: campaignId, source: 'search_terms' });
        queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'negative-keywords'] });
        queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ads'] });
      }

      if (errors.length > 0) {
        toast({ description: `Added ${totalAdded} negative keywords. ${errors.length} campaign(s) failed.`, variant: 'destructive' });
      } else {
        toast({ description: `Added ${totalAdded} negative keyword${totalAdded !== 1 ? 's' : ''} across ${termsByCampaign.size} campaign${termsByCampaign.size !== 1 ? 's' : ''}` });
      }

      // Only clear selection if at least one campaign succeeded
      if (successfulCampaignIds.length > 0) {
        clearSelection();
      }
    } catch (err) {
      toast({ description: err instanceof Error ? err.message : 'Failed to add negative keywords', variant: 'destructive' });
    } finally {
      setIsAddingNegatives(false);
    }
  };


  // Format cost with currency
  const formatCost = (cost: number) => `${currencySymbol}${cost.toFixed(2)}`;

  // Format percentage
  const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;

  // DataTable column definitions
  const columns: ColumnDef<SearchTermMetrics, unknown>[] = useMemo(() => [
    ...(isTestMode ? [{
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          checked={selectedTerms.size === filteredTerms.length && filteredTerms.length > 0}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => (e.target.checked ? selectAllVisible() : clearSelection())}
          className="rounded border-gray-300"
        />
      ),
      cell: ({ row }: { row: { original: SearchTermMetrics } }) => (
        <input
          type="checkbox"
          checked={selectedTerms.has(row.original.search_term)}
          onChange={() => toggleTermSelection(row.original.search_term)}
          className="rounded border-gray-300"
        />
      ),
      enableSorting: false,
      meta: { className: 'px-4 w-10' },
    } as ColumnDef<SearchTermMetrics, unknown>] : []),
    {
      accessorKey: 'search_term',
      header: 'Search Term',
      cell: ({ row }) => {
        const term = row.original;
        const isHighCostLowConversion = term.cost > 10 && term.conversions === 0;
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{term.search_term}</span>
            {isHighCostLowConversion && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertCircle className="h-4 w-4 text-[#B91C1C] flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent>High spend, no conversions</TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'match_type',
      header: 'Match Type',
      cell: ({ row }) => (
        <span
          className={cn(
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
            MATCH_TYPE_COLORS[row.original.match_type] || MATCH_TYPE_COLORS.UNKNOWN
          )}
        >
          {row.original.match_type}
        </span>
      ),
    },
    {
      id: 'performance',
      header: 'Performance',
      // Composite cell — sort by cost (#4271 default; flag for PM if a
      // different primary metric is wanted).
      accessorFn: (row) => row.cost,
      cell: ({ row }) => (
        <div className="flex items-center justify-center gap-3 text-sm text-gray-900">
          <span>{row.original.impressions.toLocaleString()} <span className="text-xs text-gray-500">imp</span></span>
          <span>{row.original.clicks.toLocaleString()} <span className="text-xs text-gray-500">clicks</span></span>
          <span>{row.original.conversions.toFixed(1)} <span className="text-xs text-gray-500">conv</span></span>
          <span>{formatCost(row.original.cost)} <span className="text-xs text-gray-500">cost</span></span>
          <span className="text-gray-600">{formatPercent(row.original.ctr)} <span className="text-xs text-gray-500">ctr</span></span>
        </div>
      ),
      enableSorting: true,
      meta: { className: 'px-6 text-center' },
    },
    {
      accessorKey: 'campaign_name',
      header: 'Campaign',
      cell: ({ row }) => {
        const localName = campaignNameMap.get(row.original.campaign_id);
        return (
          <div>
            <div className="text-sm text-gray-900">{localName || parseCampaignName(row.original.campaign_name)}</div>
            <div className="text-xs text-gray-500">{row.original.ad_group_name}</div>
          </div>
        );
      },
    },
  ], [selectedTerms, filteredTerms, currencySymbol, campaignNameMap, isTestMode]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="xl" className="text-gray-500" />
        <span className="ml-3 text-gray-500">Loading search terms...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    // No Google Ads account connected — the report query fails. Show a graceful
    // info state instead of a stray red error (#4725), same class as the
    // no-account handling elsewhere.
    if (!googleAdsAccount) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
          <Search className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
          <h3 className="text-lg font-semibold text-gray-500 mb-2">No Google Ads account connected</h3>
          <p className="text-gray-500">Connect a Google Ads account to see search terms from your campaigns.</p>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span>{error instanceof Error ? error.message : 'Failed to load search terms'}</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (searchTerms.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
        <Search className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
        <h3 className="text-lg font-semibold text-gray-500 mb-2">No Search Terms Yet</h3>
        <p className="text-gray-500">Search terms will appear here once your campaigns receive search impressions</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-6">
      {/* Header: count left, action right — fixed height to prevent jump */}
      <div className="flex items-center justify-between gap-3 h-9">
        <span className="text-sm text-gray-500">
          {filteredTerms.length.toLocaleString()} search term{filteredTerms.length !== 1 ? 's' : ''}
        </span>
        {isTestMode && selectedTerms.size > 0 && (
          <Button
            size="sm"
            onClick={handleAddAsNegative}
            disabled={isAddingNegatives}
            className="h-9 bg-[#FEE2E2] hover:bg-[#FDD6D6] text-[#B91C1C] border border-[#FCA5A5] shadow-none"
          >
            <Ban className="h-4 w-4 mr-1" />
            {isAddingNegatives ? 'Adding...' : `Add ${selectedTerms.size} as Negative`}
          </Button>
        )}
      </div>

      {/* Search Terms Table */}
      <DataTable
        data={filteredTerms}
        columns={columns}
        pageSize={10}
        initialSorting={[{ id: 'cost', desc: true }]}
        emptyState="No search terms match your filter"
        getRowClassName={(row) => {
          const isSelected = selectedTerms.has(row.search_term);
          const isHighCostLowConversion = row.cost > 10 && row.conversions === 0;
          return cn(
            isSelected && 'bg-[#290BD9]/10',
            isHighCostLowConversion && !isSelected && 'bg-[#FEE2E2]/50'
          ) || undefined;
        }}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mt-4">
        <Card className="p-4">
          <div className="text-sm text-gray-500">Total Impressions</div>
          <div className="text-xl font-semibold text-gray-900">
            {filteredTerms.reduce((sum, t) => sum + t.impressions, 0).toLocaleString()}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-500">Total Clicks</div>
          <div className="text-xl font-semibold text-gray-900">
            {filteredTerms.reduce((sum, t) => sum + t.clicks, 0).toLocaleString()}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-500">Total Cost</div>
          <div className="text-xl font-semibold text-gray-900">
            {formatCost(filteredTerms.reduce((sum, t) => sum + t.cost, 0))}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-500">Total Conversions</div>
          <div className="text-xl font-semibold text-gray-900">
            {filteredTerms.reduce((sum, t) => sum + t.conversions, 0).toFixed(1)}
          </div>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/_layout/optimizations/')({
  component: withOrgReset(OptimizationsPage),
  validateSearch: (search: Record<string, unknown>) => ({
    types: (search.types as string) || undefined,
    campaigns: (search.campaigns as string) || undefined,
    statuses: (search.statuses as string) || undefined,
    from: (search.from as string) || undefined,
    to: (search.to as string) || undefined,
  }),
  staticData: {
    title: 'Optimizations',
    icon: Sparkles,
  },
});

type ReviewDecision = 'Approve' | 'Reject' | 'Skip';
type ReasonCategory = 'brand_variants' | 'prospecting_terms' | 'competitors' | '';

interface ReviewData {
  manual_tag: ReviewDecision;
  reason: ReasonCategory;
  notes: string;
}

// Group actions by campaign
interface CampaignGroup {
  key: string; // platform-prefixed group key (#4284): "google-<campaign_id>" or "meta-<meta_campaign_id>"
  platform: 'google' | 'meta';
  campaign_id: number;
  meta_campaign_id?: number | null;
  campaign_name: string;
  campaign_goal?: string;
  actions: ActionLog[];
}

// Component score display name mapping (keys from backend algorithm_v3.py)
// Order: 1. Performance, 2. Intent, 3. Business Relevance, 4. Campaign Alignment, 5. Location, 6. Competitor
const COMPONENT_SCORE_LABELS = new Map<string, string>([
  ['performance', 'Campaign Performance'],
  ['intent', 'Search Intent'],
  ['business_relevance', 'Business Relevance'],
  ['campaign_alignment', 'Campaign Alignment'],
  ['llm_location', 'Location Analysis'],
  ['llm_competitor', 'Competitor Analysis'],
]);

// Get ordered component score keys for consistent display
const COMPONENT_SCORE_ORDER = Array.from(COMPONENT_SCORE_LABELS.keys());

// Action type display name mapping
const ACTION_TYPE_LABELS: Record<string, string> = {
  DELETE_KEYWORD: 'Remove Keyword',
  ADD_KEYWORD: 'Add Keyword',
  PAUSE_KEYWORD: 'Pause Keyword',
  ADD_NEGATIVE_KEYWORD: 'Add Negative',
  PAUSE_NEGATIVE_KEYWORD: 'Pause Negative',
  ADD_EXACT_MATCH_KEYWORD: 'Add Exact Match',
  SET_BUDGET: 'Set Budget',
  SET_TCPA: 'Set tCPA',
  CREATE_CAMPAIGN_WITH_ALTERNATE_LOCATION: 'Create Campaign',
  SYNC_CAMPAIGN_DATA: 'Campaign Sync',
  GOOGLE_ADS_SYNC: 'Google Sync',
  CREATE_HIPO_CAMPAIGN: 'Create High Performance Campaign',
  CREATE_LOCATION_CAMPAIGN: 'Create Location Campaign',
  CREATE_ICP_CAMPAIGN: 'Create ICP Campaign',
  UPDATE_LOCATION_HEADLINES: 'Update Headlines',
  UPDATE_LOCATION_DESCRIPTIONS: 'Update Descriptions',
  UPDATE_ICP_HEADLINES: 'Update Headlines',
  UPDATE_ICP_DESCRIPTIONS: 'Update Descriptions',
  BUILD_LANDING_PAGE: 'Build Landing Page',
  // Meta optimization actions (#4284) — without these the type filter excludes every Meta row.
  META_OBSERVE: 'Observe (Meta)',
  META_PAUSE_AD: 'Pause Ad (Meta)',
  META_UPDATE_BUDGET: 'Update Budget (Meta)',
  META_UPDATE_BIDDING: 'Update Bidding (Meta)',
  META_REFRESH_CREATIVE: 'Refresh Creative (Meta)',
  META_MOVE_AD_TO_SCALING_CAMPAIGN: 'Promote to Scaling (Meta)',
  META_DUPLICATE_AD_SET_TO_AUDIENCES: 'Duplicate Ad Set (Meta)',
};

// Action type pill colors - using brand standard status pill colors
const ACTION_TYPE_COLORS: Record<string, string> = {
  DELETE_KEYWORD: 'bg-red-100 text-red-800',           // Critical
  ADD_KEYWORD: 'bg-green-100 text-green-800',          // Success
  PAUSE_KEYWORD: 'bg-yellow-100 text-yellow-800',      // Warning
  ADD_NEGATIVE_KEYWORD: 'bg-blue-100 text-blue-800',   // Standard
  PAUSE_NEGATIVE_KEYWORD: 'bg-blue-100 text-blue-800', // Standard
  ADD_EXACT_MATCH_KEYWORD: 'bg-indigo-100 text-indigo-800', // Onboarding
  SET_BUDGET: 'bg-purple-100 text-purple-800',         // Brand
  SET_TCPA: 'bg-pink-100 text-pink-800',               // Featured
  CREATE_CAMPAIGN_WITH_ALTERNATE_LOCATION: 'bg-teal-100 text-teal-800', // Active
  SYNC_CAMPAIGN_DATA: 'bg-gray-100 text-gray-900',     // Neutral
  GOOGLE_ADS_SYNC: 'bg-gray-100 text-gray-900',        // Neutral
  CREATE_HIPO_CAMPAIGN: 'bg-teal-100 text-teal-800',   // Active
  CREATE_LOCATION_CAMPAIGN: 'bg-teal-100 text-teal-800', // Active
  CREATE_ICP_CAMPAIGN: 'bg-teal-100 text-teal-800',    // Active
  UPDATE_LOCATION_HEADLINES: 'bg-orange-100 text-orange-800', // Seasonal
  UPDATE_LOCATION_DESCRIPTIONS: 'bg-orange-100 text-orange-800', // Seasonal
  UPDATE_ICP_HEADLINES: 'bg-indigo-100 text-indigo-800', // Onboarding
  UPDATE_ICP_DESCRIPTIONS: 'bg-indigo-100 text-indigo-800', // Onboarding
  // Meta optimization actions (#4284) — cobalt to match the Meta platform badge.
  META_OBSERVE: 'bg-[#EDE9FE] text-[#290BD9]',
  META_PAUSE_AD: 'bg-[#EDE9FE] text-[#290BD9]',
  META_UPDATE_BUDGET: 'bg-[#EDE9FE] text-[#290BD9]',
  META_UPDATE_BIDDING: 'bg-[#EDE9FE] text-[#290BD9]',
  META_REFRESH_CREATIVE: 'bg-[#EDE9FE] text-[#290BD9]',
  META_MOVE_AD_TO_SCALING_CAMPAIGN: 'bg-[#EDE9FE] text-[#290BD9]',
  META_DUPLICATE_AD_SET_TO_AUDIENCES: 'bg-[#EDE9FE] text-[#290BD9]',
};

// Check if action is a negative keyword type (excluded from metrics columns per #2573)
function isNegativeKeywordAction(action: ActionLog): boolean {
  const actionType = action.action_type?.toUpperCase();
  return actionType === 'ADD_NEGATIVE_KEYWORD' ||
    actionType === 'PAUSE_NEGATIVE_KEYWORD' ||
    actionType === 'ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN';
}

// Get performance metrics from action's matched rule
function getActionMetrics(action: ActionLog) {
  return action.after_state?.matched_rules?.[0]?.performance_metrics;
}

// Helper to get confidence badge color
function getConfidenceBadgeColor(confidence: number): string {
  if (confidence >= 0.7) return 'bg-green-100 text-green-700';
  if (confidence >= 0.4) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

// Helper to get progress bar fill color based on score
// Using brand colors from /components/forms (Approve/Undo toggle) and semantic colors
function getScoreColor(score: number | string): string {
  const numScore = typeof score === 'string' ? parseFloat(score) : score;
  if (numScore >= 0.7) return '#16a34a'; // green-600 (from Approve toggle)
  if (numScore >= 0.4) return '#f59e0b'; // amber-500 - distinct yellow/orange
  return '#dc2626'; // red-600 (from Undo toggle)
}

// Helper to compare action_type case-insensitively (backend sends UPPERCASE, frontend enum is lowercase)
function isActionType(action: ActionLog, ...types: ActionType[]): boolean {
  const actionType = action.action_type?.toUpperCase();
  return types.some((t) => t.toUpperCase() === actionType);
}

// Format an action type for display: "GOOGLE_ADS_SYNC" -> "Google Ads Sync"
function formatActionTypeLabel(actionType: string): string {
  return ACTION_TYPE_LABELS[actionType] ||
    actionType.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Extract display text from an action based on entity_type.
// Designed to be future-proof: any unrecognized entity_type falls back to action.log,
// so new backend entity types display correctly without frontend changes.
function getEntityDisplayText(action: ActionLog): string {
  const entityType = action.entity_type;

  // Keyword actions: extract keyword text from structured state for cleaner display
  if (entityType === 'keyword' || entityType === 'keywords') {
    return (action.after_state?.keyword_text as string) ||
      (action.after_state?.keyword as string) ||
      (action.details?.keyword_text as string) ||
      (action.details?.keyword as string) ||
      (action.before_state?.keyword_text as string) ||
      (action.before_state?.text as string) ||
      action.log?.match(/keyword '([^']+)'/)?.[1] ||
      action.log || 'Keyword action';
  }

  // Negative keyword actions: extract keyword text from structured state
  if (entityType === 'negative_keyword' || entityType === 'negative_keywords') {
    return (action.after_state?.negative_keyword as string) ||
      (action.after_state?.keyword as string) ||
      (action.details?.negative_keyword as string) ||
      (action.details?.keyword as string) ||
      (action.before_state?.keyword_text as string) ||
      (action.before_state?.text as string) ||
      action.log?.match(/keyword '([^']+)'/)?.[1] ||
      action.log || 'Negative keyword action';
  }

  // Ad actions: display ad URL and sync status
  if (entityType === 'ad') {
    const adUrl = (action.after_state?.ad_url as string) || action.ad_url;
    const status = action.after_state?.status as string;
    const statusLabel = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Synced';
    const displayUrl = adUrl || `Ad #${action.after_state?.ad_id || action.entity_id || 'unknown'}`;
    return `${statusLabel}: ${displayUrl}`;
  }

  // Asset actions: display asset type, status, and text
  if (entityType === 'asset' || entityType === 'headline' || entityType === 'description') {
    const assetType = (action.after_state?.asset_type as string) || entityType;
    const assetLabel = assetType.charAt(0).toUpperCase() + assetType.slice(1);
    const status = action.after_state?.status as string;
    const statusLabel = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Synced';
    const assetText = (action.after_state?.asset_text as string) ||
      (action.details?.asset_text as string) ||
      action.log?.match(/'([^']+)'/)?.[1];
    return assetText ? `${statusLabel} ${assetLabel}: ${assetText}` : `${statusLabel} ${assetLabel}`;
  }

  // Funnel stage actions: display ad URL, funnel stage, and status change
  if (entityType === 'funnel_stage') {
    const afterState = action.after_state as Record<string, unknown> | undefined;
    const beforeState = action.before_state as Record<string, unknown> | undefined;
    const funnelStage = (afterState?.funnel_stage as string) || '';
    const funnelLabel = funnelStage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const adUrl = afterState?.ad_url as string;
    const oldStatus = beforeState?.status as string;
    const newStatus = afterState?.status as string;
    const adDisplay = adUrl || `Ad #${afterState?.ad_id || action.entity_id || 'unknown'}`;
    if (oldStatus && newStatus) {
      return `${funnelLabel} for ${adDisplay}: ${oldStatus} → ${newStatus}`;
    }
    return `${funnelLabel}: ${adDisplay}`;
  }

  // All other entity types (campaign, campaign_sync, location, and any future types):
  // The backend always sets action.log with a human-readable description, so use that directly.
  // This ensures new entity types work without requiring frontend changes.
  return action.log || action.action_type?.replace(/_/g, ' ') || 'Action performed';
}

// Details Panel Component
interface OptimizationDetailsPanelProps {
  action: ActionLog | null;
  currencySymbol: string;
}

function OptimizationDetailsPanel({ action, currencySymbol }: OptimizationDetailsPanelProps) {
  const navigate = useNavigate();
  if (!action) {
    return (
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 text-center">
        <Sparkles className="mx-auto h-10 w-10 text-gray-500 mb-3" strokeWidth={1} />
        <h3 className="text-sm font-semibold text-gray-500 mb-1">Select an Optimization</h3>
        <p className="text-xs text-gray-500">
          Click any row in the table to view detailed optimization analysis
        </p>
      </div>
    );
  }

  // Extract V3 algorithm data
  const matchedRule: MatchedRule | undefined = action.after_state?.matched_rules?.[0];
  const componentScores = matchedRule?.component_scores;
  const llmAnalysis = matchedRule?.llm_analysis;
  const performanceMetrics = matchedRule?.performance_metrics;
  const tags = matchedRule?.tags || [];
  const confidenceLevel = matchedRule?.confidence_level ?? 0;
  const hasConfidenceLevel = matchedRule?.confidence_level != null;
  const finalScore = matchedRule?.final_score ?? 0;

  // Get display text based on entity type
  const keywordText = getEntityDisplayText(action);

  const isSyncAction = isActionType(action, ActionType.GOOGLE_ADS_SYNC, ActionType.SYNC_CAMPAIGN_DATA);

  return (
    <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-3 space-y-3">
      {/* Header with keyword name and confidence badge */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 truncate flex-1">
          {keywordText}
        </h3>
        {!isSyncAction && hasConfidenceLevel && (
          <span className={cn(
            "text-xs font-medium px-2 py-1 rounded whitespace-nowrap",
            getConfidenceBadgeColor(confidenceLevel)
          )}>
            {Math.round(confidenceLevel * 100)}% confidence
          </span>
        )}
      </div>

      {/* Build Landing Page (#5701): show the detector's reason and jump into the campaign's Landing Page tab. */}
      {isActionType(action, ActionType.BUILD_LANDING_PAGE) && (() => {
        const slug = action.campaign_slug || action.campaign?.slug;
        const reason = action.after_state?.matched_rules?.[0]?.reason || action.reason || action.log;
        return (
          <div className="border-l-4 border-[#C4B5FD] bg-[#EDE9FE] p-3 space-y-3">
            {reason && <p className="text-xs text-gray-900">{reason}</p>}
            {slug && (
              <Button
                onClick={() => navigate({ to: `/campaigns/google/${slug}` as string, hash: 'landing-page' })}
                className="h-9 bg-[#290BD9] hover:bg-[#2309C5] text-white"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Build Landing Page
              </Button>
            )}
          </div>
        );
      })()}

      {/* Performance Metrics Grid */}
      {performanceMetrics && (
        <div>
          <div className="text-xs text-gray-700 mb-2">Performance (last 14 days)</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-xs text-gray-600">Impressions</span>
              <span className="text-xs font-medium text-gray-900">
                {performanceMetrics.impressions?.toLocaleString() ?? '-'}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-xs text-gray-600">Cost</span>
              <span className="text-xs font-medium text-gray-900">
                {currencySymbol}{performanceMetrics.cost?.toFixed(2) ?? '-'}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-xs text-gray-600">Clicks</span>
              <span className="text-xs font-medium text-gray-900">
                {performanceMetrics.clicks?.toLocaleString() ?? '-'}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-xs text-gray-600">CPC</span>
              <span className="text-xs font-medium text-gray-900">
                {currencySymbol}{performanceMetrics.cpc?.toFixed(2) ?? '-'}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-xs text-gray-600">Conversions</span>
              <span className="text-xs font-medium text-gray-900">
                {performanceMetrics.conversions?.toFixed(2) ?? '-'}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-xs text-gray-600">CVR</span>
              <span className="text-xs font-medium text-gray-900">
                {((performanceMetrics.cvr ?? 0) * 100).toFixed(2)}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Component Scores Section */}
      {componentScores && Object.keys(componentScores).length > 0 && (
        <div className="space-y-3">
          <div className="text-xs text-gray-700">Component Scores</div>
          {COMPONENT_SCORE_ORDER
            .filter(key => key in componentScores)
            .map((key) => {
              const scoreData = componentScores[key] as ComponentScore;
              return (
            <div className="space-y-1" key={key}>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-700 flex-1">
                  {COMPONENT_SCORE_LABELS.get(key) || key.replace(/_/g, ' ')}
                </span>
                <span className="text-xs font-medium text-gray-800 w-10 text-right">
                  {scoreData.score.toFixed(2)}
                </span>
                <span className="text-xs text-gray-500 w-12 text-right">
                  ({Math.round(scoreData.confidence * 100)}%)
                </span>
              </div>
              {/* Progress bar with brand colors from /components/colors status pills */}
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full transition-all duration-300"
                  style={{
                    width: `${scoreData.score * 100}%`,
                    backgroundColor: getScoreColor(scoreData.score)
                  }}
                />
              </div>
              {/* Explanation text */}
              {scoreData.reasoning && (
                <p className="text-xs text-gray-600 italic">
                  {scoreData.reasoning}
                </p>
              )}
            </div>
              );
            })}

          {/* Final Score */}
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="text-xs font-medium text-gray-900">Final Score</span>
            <span className={cn(
              "text-xs font-bold",
              finalScore < 0.4 ? 'text-red-600' : finalScore < 0.7 ? 'text-yellow-600' : 'text-green-600'
            )}>
              {finalScore.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* LLM Analysis Section (Competitor Analysis) */}
      {llmAnalysis && (llmAnalysis.is_competitor || llmAnalysis.competitor_url) && (
        <div className="border-t pt-3">
          <div className="text-xs text-gray-700 mb-2">Competitor Analysis</div>
          {llmAnalysis.competitor_url && (
            <p className="text-xs text-gray-700">
              {llmAnalysis.competitor_url} ({Math.round(llmAnalysis.competitor_closeness * 100)}% similarity)
            </p>
          )}
          {llmAnalysis.reasoning && (
            <p className="text-xs text-gray-600 italic mt-1">
              {llmAnalysis.reasoning}
            </p>
          )}
        </div>
      )}

      {/* Additional Tags Section */}
      {tags.length > 0 && (
        <div className="border-t pt-3">
          <div className="text-xs text-gray-700 mb-2">Additional tags</div>
          <div className="flex flex-wrap gap-1">
            {tags.map((tag: string, idx: number) => (
              <span
                key={idx}
                className={cn(
                  "inline-flex px-2 py-1 text-xs rounded",
                  tag.startsWith('pen')
                    ? 'bg-red-100 text-red-700'
                    : 'bg-blue-100 text-blue-700'
                )}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no algorithm analysis data */}
      {!matchedRule && (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <Sparkles className="h-10 w-10 text-gray-500 mb-3" strokeWidth={1} />
          <h3 className="text-sm font-semibold text-gray-500 mb-1">No Analysis Data</h3>
          <p className="text-xs text-gray-500">
            Algorithm analysis not available for this action
          </p>
        </div>
      )}
    </div>
  );
}

function OptimizationsPage() {
  const organizationData = useAtomValue(organizationDataAtom);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const posthog = usePostHog();
  const { symbol: _cs } = useCurrencySymbol();
  const currencySymbol = _cs ?? '€';
  const isTestMode = useTestMode();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = Route.useSearch();

  const { activeTab, handleTabChange } = useTabPersistence(
    selectedOptimizationsTabAtom,
    TAB_IDS,
    'actions',
    (tabId, replace) => navigate({ to: '/optimizations', hash: tabId, replace, search: { types: searchParams.types || undefined, campaigns: searchParams.campaigns || undefined, statuses: searchParams.statuses || undefined, from: searchParams.from || undefined, to: searchParams.to || undefined } }),
    location.hash,
  );

  // Search terms controls (lifted to parent for TabCardPanel actions slot)
  const [searchTermsDays, setSearchTermsDays] = useState(30);
  const [searchTermsFilter, setSearchTermsFilter] = useState('');

  // Meta optimizations are admin-only to apply (#4284); Google stays open to any org member.
  const { user } = useUserProfile();
  const isAdmin = user?.role === UserRole.ADMIN;

  // State for review decisions
  const [reviewData, setReviewData] = useState<Record<number, ReviewData>>({});

  // State for selected action (for details panel)
  const [selectedActionId, setSelectedActionId] = useState<number | null>(null);


  // State for sorting within campaign groups
  const [sortKey, setSortKey] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | false>('desc');

  const numericSortKeys = new Set(['impressions', 'clicks', 'cpc', 'cost']);
  const handleSort = useCallback((key: string) => {
    setSortKey((prev) => {
      const defaultDir = numericSortKeys.has(key) ? 'desc' : 'asc';
      const reverseDir = defaultDir === 'desc' ? 'asc' : 'desc';
      if (prev !== key) { setSortDir(defaultDir); return key; }
      setSortDir((d) => d === defaultDir ? reverseDir : d === reverseDir ? false : defaultDir);
      return key;
    });
  }, []);

  const getSortDirection = useCallback(
    (key: string): 'asc' | 'desc' | false => (sortKey === key ? sortDir : false),
    [sortKey, sortDir]
  );

  // Default date range: last 7 days
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  }, []);
  const defaultTo = useMemo(() => new Date().toISOString().split('T')[0], []);

  // Fetch action history for the current organization
  const queryFrom = searchParams.from || defaultFrom;
  const queryTo = searchParams.to || defaultTo;

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['optimizations', 'history', organizationData?.id, queryFrom, queryTo],
    queryFn: () =>
      fetchAllActionLogs(
        { organization_id: organizationData?.id, start_date: queryFrom, end_date: queryTo },
        { skip: 0, limit: 250 }
      ),
    enabled: !!organizationData?.id,
    staleTime: 30000, // 30 seconds
  });

  const actionLogs = response?.items ?? [];

  // #6257: Meta rows are gated behind test mode until Meta App Review clears — hide them
  // entirely (stats, feed groups, and campaign/type filters all derive from this single source),
  // reviving the pre-GA rule from #4284.
  const visibleActionLogs = useMemo(
    () => (isTestMode ? actionLogs : actionLogs.filter((a) => a.platform !== 'meta')),
    [actionLogs, isTestMode],
  );

  // Derive available campaigns from the visible logs. Meta rows carry a null campaign_id and are
  // skipped below.
  const availableCampaigns = useMemo(() => {
    const seen = new Map<number, string>();
    visibleActionLogs.forEach((action) => {
      if (action.campaign_id == null) return; // Meta rows have no Google campaign id (#4284)
      if (!seen.has(action.campaign_id)) {
        seen.set(
          action.campaign_id,
          action.campaign_name || action.campaign?.name || `Campaign ${action.campaign_id}`,
        );
      }
    });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [visibleActionLogs]);

  // Parse filter values from URL search params
  // Default: all types selected except sync types (campaign-level and entity-level reverse sync noise)
  const SYNC_TYPES = ['SYNC_CAMPAIGN_DATA', 'GOOGLE_ADS_SYNC'];
  const defaultTypes = Object.keys(ACTION_TYPE_LABELS).filter((t) => !SYNC_TYPES.includes(t));
  const filterTypes = searchParams.types === 'none' ? [] : searchParams.types ? searchParams.types.split(',') : defaultTypes;
  const filterCampaignIds = searchParams.campaigns ? searchParams.campaigns.split(',') : [];
  const filterFrom = searchParams.from || defaultFrom;
  const filterTo = searchParams.to || defaultTo;
  const defaultStatuses = ['applied', 'pending'];
  const filterStatuses = searchParams.statuses === 'none' ? [] : searchParams.statuses ? searchParams.statuses.split(',') : defaultStatuses;

  // Get selected action
  const selectedAction = useMemo(() => {
    if (!selectedActionId) return null;
    return actionLogs.find(action => action.id === selectedActionId) || null;
  }, [selectedActionId, actionLogs]);

  // Group actions by campaign, sorted within each group, with client-side filtering
  const campaignGroups = useMemo(() => {
    // Apply filters (visibleActionLogs already excludes Meta rows when not in test mode).
    let filtered = visibleActionLogs;

    filtered = filtered.filter((a) => filterTypes.length === 0 ? false : filterTypes.includes(a.action_type));
    filtered = filtered.filter((a) => {
      if (filterStatuses.length === 0) return false;
      const isRec = a.source === 'recommendation';
      const isUndone = a.is_undone;
      // Only actions that still have a Review toggle are "pending review":
      // a reviewed action no longer shows a toggle, so it must not count as
      // pending — it falls into "applied". #4165
      const isReviewed = !!a.audit_results;
      if (isUndone && filterStatuses.includes('undone')) return true;
      if (isRec && !isUndone && !isReviewed && filterStatuses.includes('pending')) return true;
      if ((!isRec || isReviewed) && !isUndone && filterStatuses.includes('applied')) return true;
      return false;
    });
    if (filterCampaignIds.length > 0) {
      filtered = filtered.filter((a) => filterCampaignIds.includes(String(a.campaign_id)));
    }
    if (filterFrom) {
      filtered = filtered.filter((a) => a.created_at >= filterFrom);
    }
    if (filterTo) {
      // Include the entire end day
      const toEndOfDay = filterTo + 'T23:59:59.999Z';
      filtered = filtered.filter((a) => a.created_at <= toEndOfDay);
    }

    const groups: Record<string, CampaignGroup> = {};

    filtered.forEach((action) => {
      // Group by platform + the platform-appropriate campaign id (#4284): Meta rows have a NULL
      // campaign_id, so keying on campaign_id alone would collapse them all into one bogus group.
      const isMeta = action.platform === 'meta';
      const key = isMeta ? `meta-${action.meta_campaign_id}` : `google-${action.campaign_id}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          platform: isMeta ? 'meta' : 'google',
          campaign_id: action.campaign_id,
          meta_campaign_id: action.meta_campaign_id ?? null,
          campaign_name:
            action.campaign_name ||
            action.campaign?.name ||
            (isMeta ? `Meta Campaign ${action.meta_campaign_id}` : `Campaign ${action.campaign_id}`),
          campaign_goal: action.campaign_goal,
          actions: [],
        };
      }
      groups[key].actions.push(action);
    });

    // Sort actions within each group
    if (sortDir && sortKey) {
      const getValue = (action: ActionLog): string | number => {
        if (sortKey === 'created_at') return action.created_at;
        if (sortKey === 'campaign_goal') return action.campaign_goal || '';
        if (sortKey === 'action_type') return action.action_type;
        const metrics = getActionMetrics(action);
        if (sortKey === 'impressions') return metrics?.impressions ?? -1;
        if (sortKey === 'clicks') return metrics?.clicks ?? -1;
        if (sortKey === 'cpc') return metrics?.cpc ?? -1;
        if (sortKey === 'cost') return metrics?.cost ?? -1;
        return '';
      };
      for (const group of Object.values(groups)) {
        group.actions.sort((a, b) => {
          const aVal = getValue(a);
          const bVal = getValue(b);
          const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }
    }

    return Object.values(groups);
  }, [visibleActionLogs, sortKey, sortDir, filterTypes, filterCampaignIds, filterFrom, filterTo]);

  // Mutation for confirming optimizations
  const confirmMutation = useMutation({
    mutationFn: (actions: ConfirmOptimizationAction[]) =>
      confirmOptimizations({ actions }),
    onSuccess: (data) => {
      posthog?.capture('optimizations_confirmed', { action_count: data.confirmed });
      // Some actions can be rejected by Google Ads while others apply (#5755) — surface those
      // per-action failures rather than reporting a clean success.
      if (data.errors && data.errors.length > 0) {
        toast({
          title: `${data.errors.length} optimization${data.errors.length !== 1 ? 's' : ''} not applied`,
          description: data.errors.join('; '),
          variant: 'destructive',
        });
      }
      if (data.confirmed > 0) {
        toast({
          title: 'Optimizations Confirmed',
          description: `Successfully confirmed ${data.confirmed} optimization${data.confirmed !== 1 ? 's' : ''}`,
        });
      }
      // Clear review data for confirmed actions
      setReviewData({});
      setSelectedActionId(null);
      // Refetch the list
      refetch();
      queryClient.invalidateQueries({ queryKey: ['optimizations'] });
    },
    onError: (err: Error) => {
      toast({
        title: 'Error',
        description: err.message || 'Failed to confirm optimizations',
        variant: 'destructive',
      });
    },
  });

  // Get review data for an action (with defaults)
  const getReviewData = (actionId: number): ReviewData => {
    if (reviewData[actionId]) return reviewData[actionId];
    // PROPOSED recommendations default to 'Reject' (not yet applied); ActionLogs default to 'Approve' (already applied)
    const action = actionLogs.find(a => a.id === actionId);
    const defaultTag: ReviewDecision = action?.source === 'recommendation' ? 'Reject' : 'Approve';
    return { manual_tag: defaultTag, reason: '', notes: '' };
  };

  // Handle toggle between Approve and Undo
  const handleToggle = (action: ActionLog, newValue: 'left' | 'middle' | 'right') => {
    const tagMap: Record<string, ReviewDecision> = { left: 'Approve', middle: 'Skip', right: 'Reject' };
    const newTag = tagMap[newValue];
    const current = getReviewData(action.id);
    posthog?.capture('optimization_action_toggled', { action_id: action.id, decision: newTag });

    let newNotes = '';
    let newReason: ReasonCategory = '';

    if (newTag === 'Reject') {
      newNotes = getEntityDisplayText(action);

      switch (action.campaign_goal) {
        case 'brand':
          newReason = 'brand_variants';
          break;
        case 'competitor':
          newReason = 'competitors';
          break;
        default:
          newReason = 'prospecting_terms';
      }
    }

    setReviewData((prev) => ({
      ...prev,
      [action.id]: {
        ...current,
        manual_tag: newTag,
        notes: newNotes,
        reason: newReason,
      },
    }));
  };

  // Handle confirm for a single campaign — skip items in 'Skip' state
  const handleConfirmCampaign = (campaignGroup: CampaignGroup) => {
    const actions: ConfirmOptimizationAction[] = [];
    for (const action of campaignGroup.actions) {
      const data = getReviewData(action.id);
      if (data.manual_tag === 'Skip') continue;
      const manual_tag: 'Approve' | 'Undo' | 'Reject' =
        data.manual_tag === 'Reject' ? 'Undo' : data.manual_tag === 'Approve' ? 'Approve' : 'Undo';
      actions.push({
        action_id: action.id,
        manual_tag,
        reason: data.reason,
        notes: data.notes,
      });
    }

    if (actions.length > 0) {
      confirmMutation.mutate(actions);
    }
  };

  // Check if action can be toggled (undone or approved)
  const canUndo = (action: ActionLog): boolean => {
    // PROPOSED recommendations (from Colossus review mode) are always toggleable
    if (action.source === 'recommendation') return true;
    // Applied ActionLogs: only a whitelist is reversible. ADD_NEGATIVE_KEYWORD (Google), plus the two
    // cleanly-reversible Meta actions — Pause Ad (re-activate) and Update Budget (restore prior budget).
    // Other applied Meta actions are audit-only, so they get no undo toggle: Promote/Duplicate created
    // live Meta entities (undo would be a destructive delete), and Observe/Refresh never mutated anything.
    // This mirrors MetaActionExecutor.undo on the backend (#5659 5b).
    const UNDOABLE_ACTION_TYPES = ['ADD_NEGATIVE_KEYWORD', 'META_PAUSE_AD', 'META_UPDATE_BUDGET'];
    return UNDOABLE_ACTION_TYPES.includes(action.action_type as string);
  };

  // Format date as "13th Jan"
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });

    // Add ordinal suffix
    const ordinal = (n: number) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return `${ordinal(day)} ${month}`;
  };

  // Get sync subtitle based on entity type.
  // Future-proof: unknown entity types get formatted from snake_case to Title Case.
  const getSyncSubtitle = (action: ActionLog): string => {
    const entityLabels: Record<string, string> = {
      keyword: 'Keyword',
      keywords: 'Keywords',
      negative_keyword: 'Negative keyword',
      negative_keywords: 'Negative keywords',
      campaign: 'Campaign',
      campaign_sync: 'Campaign',
      ad: 'Ad',
      headline: 'Headline',
      description: 'Description',
      asset: 'Asset',
      location: 'Location',
      funnel_stage: 'Funnel stage',
    };
    const entityType = action.entity_type || '';
    const label = entityLabels[entityType] ||
      entityType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) ||
      'Data';
    return `${label} synced from Google Ads changes`;
  };

  // Get action description
  const getActionDescription = (action: ActionLog): string => {
    return getEntityDisplayText(action);
  };

  // Helper to render Actions tab content
  const renderActionsContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Spinner size="xl" className="text-gray-400" />
          <span className="ml-3 text-gray-500">Loading optimizations...</span>
        </div>
      );
    }

    if (actionLogs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
          <Sparkles className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
          <h3 className="text-lg font-semibold text-gray-500 mb-2">No Optimizations Yet</h3>
          <p className="text-gray-500">Optimizations will appear here as campaign performance is analyzed</p>
        </div>
      );
    }

    if (campaignGroups.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
          <Sparkles className="h-12 w-12 text-gray-300 mb-4" strokeWidth={1} />
          <h3 className="text-lg font-semibold text-gray-500 mb-2">No Matching Optimizations</h3>
          <p className="text-gray-500">No optimizations match the current filters. Try adjusting the date range or filter criteria.</p>
        </div>
      );
    }

    // Main actions content
    return (
      <div className="space-y-6">
        {/* Campaign tables */}
        <div>
            <div className="space-y-6">
              {campaignGroups.map((group) => (
                <div key={group.key}>
                  <div className="flex items-center justify-between mb-2 px-6">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {group.campaign_name}
                      </h3>
                      <img
                        src={group.platform === 'meta' ? '/meta.png' : '/google-ads.svg'}
                        alt={group.platform === 'meta' ? 'Meta' : 'Google'}
                        title={group.platform === 'meta' ? 'Meta' : 'Google'}
                        className="h-5 w-5 object-contain shrink-0"
                      />
                      {group.campaign_goal && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {group.campaign_goal}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {group.actions.length} action{group.actions.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Table + Footer */}
                  <div className="border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="pl-6 pr-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '8%' }}>
                            <SortableHeader sortDirection={getSortDirection('created_at')} onSort={() => handleSort('created_at')}>
                              Date
                            </SortableHeader>
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '8%' }}>
                            <SortableHeader sortDirection={getSortDirection('action_type')} onSort={() => handleSort('action_type')}>
                              Action
                            </SortableHeader>
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '39%' }}>
                            Details
                          </th>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '22%' }}>
                            Performance
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '8%' }}>
                            <SortableHeader sortDirection={getSortDirection('cost')} onSort={() => handleSort('cost')}>
                              Impact
                            </SortableHeader>
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '7%' }}>
                            Review
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {group.actions.map((action) => {
                          const reviewDataForAction = getReviewData(action.id);
                          const isUndoable = canUndo(action);
                          const isReviewed = !!action.audit_results;
                          const isSelected = selectedActionId === action.id;

                          return (
                            <tr
                              key={action.id}
                              onClick={() => setSelectedActionId(action.id)}
                              className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-[#290BD9]/10' : ''}`}
                            >
                              <td className="pl-6 pr-3 py-4 whitespace-nowrap text-sm font-normal text-gray-500">
                                {formatDate(action.created_at)}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ACTION_TYPE_COLORS[action.action_type] || 'bg-gray-100 text-gray-900'}`}>
                                  {formatActionTypeLabel(action.action_type)}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-base font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                                  <span>{getActionDescription(action)}</span>
                                  {(() => {
                                    const afterState = action.after_state as { match_type?: string; added_negative_keywords?: Array<{ match_type?: string }> } | undefined;
                                    const mt = afterState?.match_type ?? afterState?.added_negative_keywords?.[0]?.match_type;
                                    if (!mt || !/KEYWORD|NEGATIVE/.test(action.action_type)) return null;
                                    const key = mt.toUpperCase();
                                    return (
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${MATCH_TYPE_COLORS[key] || MATCH_TYPE_COLORS.UNKNOWN}`}>
                                        {key}
                                      </span>
                                    );
                                  })()}
                                </div>
                                {(action.after_state?.matched_rules?.[0]?.reason || isActionType(action, ActionType.GOOGLE_ADS_SYNC, ActionType.SYNC_CAMPAIGN_DATA)) && (
                                  <div className="text-sm font-normal text-gray-500">
                                    {action.after_state?.matched_rules?.[0]?.reason || getSyncSubtitle(action)}
                                  </div>
                                )}
                              </td>
                              {(() => {
                                const isNeg = isNegativeKeywordAction(action);
                                const perf = getActionMetrics(action);
                                return (
                                  <>
                                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {isNeg ? <div className="text-center">-</div> : (
                                        <div className="grid grid-cols-3 text-center">
                                          <div
                                            className="flex flex-col items-center cursor-pointer hover:text-[#290BD9]"
                                            role="button"
                                            tabIndex={0}
                                            aria-label="Sort by impressions"
                                            onClick={(e) => { e.stopPropagation(); handleSort('impressions'); }}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSort('impressions');
                                              }
                                            }}
                                          >
                                            <span>{perf?.impressions?.toLocaleString() ?? '-'}</span>
                                            <span className="text-[10px] text-gray-400">imp</span>
                                          </div>
                                          <div
                                            className="flex flex-col items-center cursor-pointer hover:text-[#290BD9]"
                                            role="button"
                                            tabIndex={0}
                                            aria-label="Sort by clicks"
                                            onClick={(e) => { e.stopPropagation(); handleSort('clicks'); }}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSort('clicks');
                                              }
                                            }}
                                          >
                                            <span>{perf?.clicks?.toLocaleString() ?? '-'}</span>
                                            <span className="text-[10px] text-gray-400">clicks</span>
                                          </div>
                                          <div
                                            className="flex flex-col items-center cursor-pointer hover:text-[#290BD9]"
                                            role="button"
                                            tabIndex={0}
                                            aria-label="Sort by CPC"
                                            onClick={(e) => { e.stopPropagation(); handleSort('cpc'); }}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSort('cpc');
                                              }
                                            }}
                                          >
                                            <span>{perf?.cpc != null ? `${currencySymbol}${perf.cpc.toFixed(2)}` : '-'}</span>
                                            <span className="text-[10px] text-gray-400">cpc</span>
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                                      {isNeg ? '-' : perf?.cost != null ? `${currencySymbol}${perf.cost.toFixed(2)}` : '-'}
                                    </td>
                                  </>
                                );
                              })()}
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                {isReviewed ? (
                                  <span className="text-xs font-medium text-gray-400">{action.audit_results?.decision}</span>
                                ) : isUndoable ? (
                                  <ToggleSwitch
                                    value={reviewDataForAction.manual_tag === 'Approve' ? 'left' : reviewDataForAction.manual_tag === 'Reject' ? 'right' : 'middle'}
                                    leftLabel="Approve"
                                    rightLabel="Reject"
                                    onChange={(v) => handleToggle(action, v)}
                                  />
                                ) : (
                                  // Non-undoable, non-reviewed: purely informational —
                                  // nothing to review/undo, so no toggle and no bare "-". #4165
                                  <span className="text-xs font-medium text-gray-400">Applied</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer with Confirm button — only shown when there are unreviewed actions */}
                  {group.actions.some(a => !a.audit_results) && (
                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-end">
                    {group.platform === 'meta' && !isAdmin ? (
                      // Applying Meta optimizations is admin-only (#4284); the backend rejects non-admins.
                      <span className="text-sm text-gray-500">Meta optimizations can only be applied by an admin.</span>
                    ) : (
                      <Button
                        onClick={() => handleConfirmCampaign(group)}
                        disabled={confirmMutation.isPending}
                        className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
                      >
                        {confirmMutation.isPending ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Confirming...
                          </>
                        ) : (
                          (() => {
                            const unreviewedCount = group.actions.filter(a => !a.audit_results).length;
                            return `Confirm ${unreviewedCount} Action${unreviewedCount !== 1 ? 's' : ''}`;
                          })()
                        )}
                      </Button>
                    )}
                  </div>
                  )}
                  </div>
                </div>
              ))}
            </div>
          </div>
      </div>
    );
  };

  // Page-level error state when the primary optimizations query fails to load
  if (error) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3"></div>
        </div>
        <div className="flex-1">
          <PageErrorState
            title="Couldn't Load Optimizations"
            description="This page failed to load. This is a loading error, not missing data, so nothing has been lost. Retry, or contact support if it persists."
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  // Main page render with tabs
  return (
    <div className="flex flex-col h-full">
      {/* Header spacer */}
      <div className="py-0.5" />

      {/* Summary Metrics - only visible in test mode */}
      {isTestMode && (
        <div className="mb-3">
          <OptimizationSummaryMetrics currencySymbol={currencySymbol} actionLogs={visibleActionLogs} />
        </div>
      )}

      {/* Tabs + Filters */}
      <div className="flex-1 relative">
       {/* Filter positioned at top-right of entire section, above flex layout */}
       {activeTab === 'actions' && (
         <div className="absolute top-1 right-0 z-10">
           <FilterDropdown
              sections={[
                {
                  id: 'statuses',
                  title: 'Status',
                  options: [
                    { value: 'applied', label: 'Applied' },
                    { value: 'pending', label: 'Pending Review' },
                    { value: 'undone', label: 'Undone' },
                  ],
                },
                {
                  id: 'campaigns',
                  title: 'Campaign',
                  options: availableCampaigns.map((c) => ({
                    value: String(c.id),
                    label: c.name,
                  })),
                },
                {
                  id: 'types',
                  title: 'Type',
                  options: Object.entries(ACTION_TYPE_LABELS).map(([value, label]) => ({ value, label })),
                },
              ]}
              selectedValues={{
                statuses: filterStatuses,
                campaigns: filterCampaignIds,
                types: filterTypes,
              }}
              startDate={filterFrom}
              endDate={filterTo}
              defaultSelections={{
                statuses: defaultStatuses,
                types: defaultTypes,
                campaigns: [],
              }}
              onApply={(filters: FilterDropdownState) => {
                const types = filters.selections.types || [];
                const campaigns = filters.selections.campaigns || [];
                const statuses = filters.selections.statuses || [];
                navigate({
                  to: '/optimizations',
                  hash: activeTab,
                  search: {
                    types: types.length > 0 ? types.join(',') : 'none',
                    campaigns: campaigns.length > 0 ? campaigns.join(',') : undefined,
                    statuses: statuses.length > 0 ? statuses.join(',') : 'none',
                    from: filters.startDate || undefined,
                    to: filters.endDate || undefined,
                  },
                });
              }}
            />
         </div>
       )}
       <div className="flex gap-1 items-start">
       <div className="flex-1 min-w-0">
        <TabCardPanel
          tabs={tabs.map((tab) => ({
            id: tab.id,
            label: tab.label,
            isActive: activeTab === tab.id,
            onClick: () => handleTabChange(tab.id),
          }))}
          actions={activeTab === 'search-terms' ? (
            <div className="flex items-center gap-2 min-w-0 flex-shrink">
              <Select value={searchTermsDays.toString()} onValueChange={(v) => setSearchTermsDays(Number(v))}>
                <SelectTrigger className="w-32 h-9 bg-white flex-shrink-0">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative min-w-0 flex-shrink">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchTermsFilter}
                  onChange={(e) => setSearchTermsFilter(e.target.value)}
                  className="pl-9 w-full min-w-[120px] max-w-[240px] h-9 bg-white"
                />
              </div>
            </div>
          ) : undefined}
          cardClassName="flex-1"
          contentClassName="px-0 py-6"
          noPadding
        >
          {activeTab === 'actions' && renderActionsContent()}
          {activeTab === 'search-terms' && <SearchTermsTab currencySymbol={currencySymbol} days={searchTermsDays} searchFilter={searchTermsFilter} />}
        </TabCardPanel>

       </div>
        {/* Details panel — always visible on actions tab to prevent layout shift */}
        {activeTab === 'actions' && (
          <div className="w-1/4 shrink-0 self-start pt-10">
            {selectedAction ? (
              <div className="sticky top-10">
                <OptimizationDetailsPanel action={selectedAction} currencySymbol={currencySymbol} />
              </div>
            ) : (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                <Sparkles className="mx-auto h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                <h3 className="text-lg font-semibold text-gray-500 mb-2">Optimization Details</h3>
                <p className="text-gray-500">Click a row for a detailed overview</p>
              </div>
            )}
          </div>
        )}
       </div>
      </div>
    </div>
  );
}
